import { createContext, useCallback, useState, useContext, ReactNode, useRef } from 'react';
import uuid from 'react-uuid';

type UseMessageContext = {
  openMessage?: (text: string, status: MESSAGE_STATUS, error?: any) => void;
  openedMessages?: OpenedMessage[];
  closeMessage?: (id: string) => void;
};

export enum MESSAGE_STATUS {
  INFO = 'info',
  ERROR = 'error',
  SUCCESS = 'success'
}

export interface OpenedMessage {
  id: string;
  text: string | ReactNode;
  status: MESSAGE_STATUS;
  closed: boolean;
}

type AllMessageContextValuesGetter = () => UseMessageContext;

export const MessageContext = createContext<UseMessageContext>({} as UseMessageContext);

export const useInitMessage: AllMessageContextValuesGetter = () => {
  const [openedMessages, setOpenedMessages] = useState<OpenedMessage[]>([]);
  const openedMessagesRef = useRef<OpenedMessage[]>([]);

  const closeMessage = useCallback((id: string) => {
    setOpenedMessages((oldMessages) => oldMessages.filter((el) => el.id !== id));
    openedMessagesRef.current = openedMessagesRef.current.filter((el) => el.id !== id);
  }, []);

  const openMessage = useCallback((text: string, status: MESSAGE_STATUS, error?: any) => {
    const errorMessage = error?.response?.data?.message;

    if (status === MESSAGE_STATUS.ERROR) {
      const isRepeated = openedMessagesRef.current.find((message) => message.text === text);
      const curlRegex = /\bcurl\b/;
      if (!!isRepeated === false && !curlRegex.test(text)) {
        setOpenedMessages((messages) => [
          {
            id: error ? errorMessage : text,
            text: error ? errorMessage : text,
            status,
            closed: false
          },
          ...messages
        ]);
        openedMessagesRef.current.push({
          id: text,
          text: error ? errorMessage : text,
          status,
          closed: false
        });
        return;
      }
    } else {
      setOpenedMessages((messages) => [{ id: uuid(), text, status, closed: false }, ...messages]);
      openedMessagesRef.current.push({
        id: uuid(),
        text,
        status,
        closed: false
      });
    }
  }, []);

  return { openMessage, openedMessages, closeMessage };
};

/**
 * Используется для управления сообщениями
 *
 * @returns объект c 3 функциями:
 *
 * openMessage: Функция, которая используется для открытия нового сообщения. Она принимает три параметра:
  - text (string): Текстовое содержимое сообщения.
  - status (MESSAGE_STATUS): Статус сообщения, который может иметь одно из следующих значений: INFO, ERROR или SUCCESS.
  - error (optional): Необязательный объект ошибки, связанный с сообщением.

 * openMessages (опционально): Массив объектов OpenedMessage, представляющих открытые в данный момент сообщения. Каждый объект OpenedMessage имеет следующие свойства:
  - id (строка): Уникальный идентификатор сообщения.
  - text (string | ReactNode): Текстовое содержимое сообщения.
  - status (MESSAGE_STATUS): Статус сообщения.
  - closed (boolean): Булево значение, указывающее, закрыто ли сообщение.

 * closeMessage (закрыть сообщение): Функция, используемая для закрытия сообщения. Принимает один параметр:
  - id (строка): Уникальный идентификатор закрываемого сообщения.

 */

export const useMessageContext: AllMessageContextValuesGetter = () => {
  return useContext(MessageContext);
};
